import {
  DragEventHandler,
  FC,
  MouseEventHandler,
  ReactNode,
  TouchEventHandler,
  useLayoutEffect,
  useRef
} from 'react'

import cn from 'clsx'

import styles from './MarketplaceTileShell.module.scss'

interface MarketplaceTileShellProps {
  inUse: boolean
  isDragging: boolean
  name: ReactNode
  children: ReactNode
  draggable?: boolean
  title?: string
  onDragStart?: DragEventHandler<HTMLDivElement>
  onDragEnd?: DragEventHandler<HTMLDivElement>
  onContextMenu?: MouseEventHandler<HTMLDivElement>
  onTouchStart?: TouchEventHandler<HTMLDivElement>
  onTouchMove?: TouchEventHandler<HTMLDivElement>
  onTouchEnd?: TouchEventHandler<HTMLDivElement>
  onTouchCancel?: TouchEventHandler<HTMLDivElement>
}

export const MarketplaceTileShell: FC<MarketplaceTileShellProps> = ({
  inUse,
  isDragging,
  name,
  children,
  draggable,
  title,
  onDragStart,
  onDragEnd,
  onContextMenu,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel
}) => {
  const previewRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const preview = previewRef.current
    if (!preview) return

    // The gallery uses one-pixel grid rows. Include the existing bottom margin
    // so variable-height previews pack tightly without changing their sizes.
    const updateRowSpan = () => {
      const gap = parseFloat(getComputedStyle(preview).marginBottom) || 0
      preview.style.gridRowEnd = `span ${Math.max(1, Math.ceil(preview.offsetHeight + gap))}`
    }
    updateRowSpan()
    const observer = new ResizeObserver(updateRowSpan)
    observer.observe(preview)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={previewRef}
      className={cn(styles.preview, {
        [styles.inUse]: inUse,
        [styles.dragging]: isDragging
      })}
    >
      <div
        className={styles.previewInner}
        draggable={draggable}
        title={title}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onContextMenu={onContextMenu}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
      >
        {children}
      </div>
      <div className={styles.name}>{name}</div>
    </div>
  )
}
